// Builds the IsraelGives hosted-payment iframe target + POST fields.
//
// We POST the donor + donation details and IsraelGives renders ONLY the
// (PCI-compliant) credit-card fields inside the frame, collects the card,
// processes the charge, then redirects the frame to `successurl`. Card data
// never touches this app. See the "Processing Donations using the IsraelGives
// iFrame" section of the IsraelGives API docs.

import { currencyMeta, DEFAULT_CURRENCY } from './currencies';

// Documented IsraelGives payment endpoint is /pay/makedonation (per their API
// docs + every real form, incl. Standing Together's own). The old /donate/
// alias rendered a form but failed charges with a generic "unspecified error".
const IG_IFRAME_URL = 'https://secured.israelgives.org/pay/makedonation';
// Standing Together's real IsraelGives Amuta id. The build-time env override
// stays for flexibility, but the fallback is now the PRODUCTION id (not the
// 580484681 TEST id) so a missing env var can never ship test-mode again.
const IG_AMUTA_ID = process.env.REACT_APP_IG_AMUTA_ID || '515495067';

// Opaque per-attempt id tying the donor details we park on our server at
// 'initiated' to the completion that comes back through `successurl`. It is
// the ONLY thing that survives the round trip, so it must be generated once
// per donation attempt and reused for both — never regenerated per render.
export function newPaymentToken() {
  const c = window.crypto;
  if (c && c.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

// The frame redirects here (same-origin) on success; the page reports the
// completion to our backend and postMessages the result back to the app.
// Placeholders in {curly braces} are substituted by IsraelGives. `successurl`
// must be the LAST query param and URL-encoded.
//
// `t` is the token above. `r` is the path to return the donor to when this
// page loads OUTSIDE our iframe — which is what the PayPal leg does, since
// PayPal serves X-Frame-Options: SAMEORIGIN and cannot render in a frame.
// Neither carries anything donor-identifying: the details stay server-side.
function buildSuccessUrl(token, returnPath) {
  return (
    `${window.location.origin}/ig-complete.html` +
    `?tdonid={donid}&tsum={sum}&tfrequency={frequency}` +
    `&t=${encodeURIComponent(token || '')}` +
    `&r=${encodeURIComponent(returnPath || '/')}`
  );
}

// Is this an Israeli number? Either the local 0-prefixed form or the same
// number written internationally as +972.
//
// This is what decides which normalizer below runs, and it has to be the NUMBER
// that decides it rather than the donation's currency. Those are different
// questions: most overseas donors to an Israeli NGO give in shekels, and
// keying off the currency sent every one of them through the Israeli rule —
// which truncates. See normalizeIlPhone.
//
// The length guard keeps a foreign number that happens to start with a trunk 0
// (0044…, or a French 0X number) from being read as Israeli: Israeli numbers
// are 9 or 10 digits, and 0044 20 7123 4567 is 14.
export function isIsraeliPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('972')) return true;
  return /^0\d{8,9}$/.test(digits);
}

// IsraelGives rejects any phone that doesn't start with 0 or exceeds 10
// digits. Browser autofill (and some manual input) supplies the
// international form instead (972502714214 or +972-50-271-4214), so strip
// non-digits and swap a leading 972 country code for the local 0 prefix.
//
// That rule is ISRAELI-number-shaped, and the truncation at the end is only
// safe on an Israeli number. Run a foreign one through it and it comes out
// mangled — +44 20 7123 4567 becomes "4420712345", a number that belongs to
// nobody — so isIsraeliPhone above keeps foreign numbers out of here.
function normalizeIlPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  const local = digits.startsWith('972') ? `0${digits.slice(3)}` : digits;
  return local.slice(0, 10);
}

// Strip formatting but keep the number whole, including its leading +.
//
// Deliberately NOT clipped to the 11 characters their phone input declares as
// maxlength: "+44 20 7123 4567" is 13, and cutting it to 11 yields
// "+4420712345" — a valid-looking number belonging to somebody else. maxlength
// is a typing constraint on their form, not a rule the value we POST has to
// satisfy. Their own client-side phone check is dead code on a card donation
// (it sits inside a branch that only runs when no alternative engine is
// selected, yet is gated on bit being the selected engine), so a longer foreign
// number isn't checked against anything there. Gramsci gets the donor's phone
// from our own parked payload either way, so this only affects IsraelGives'
// record — where a whole number is worth more than a short wrong one.
function normalizeForeignPhone(phone) {
  return String(phone || '').replace(/[^\d+]/g, '');
}

// "Cover the transaction fee" formula. IsraelGives has no URL param to
// default their own cover-fee checkbox to checked (only hidecovermyfee=1 to
// remove it), so we hide theirs and drive our own checkbox + amount instead.
// IsraelGives' processing fee is ₪1.9 fixed + 3.9% of the donation amount; the
// fixed part is carried per-currency in currencies.js (`feeFixed`).
const FEE_COVER_RATE = 0.039;

// Extra amount to add on top of `amount` so the org receives the full
// donation after IsraelGives' processing fee is deducted. Denominated in the
// donation's own currency — a $50 donation is topped up in dollars.
export function calcFeeCoverAmount(amount, currency = DEFAULT_CURRENCY) {
  const numeric = Number(amount) || 0;
  if (numeric <= 0) return 0;
  const fixed = currencyMeta(currency).feeFixed;
  return Math.round((fixed + numeric * FEE_COVER_RATE) * 100) / 100;
}

// frequency: '1' = one-time, '2' = recurring monthly, '3' = annual.
// currency: an ISO code from currencies.js ('ILS' default).
// projectId: one of the amuta's own "projects and designations" (backoffice →
//   /he/Allocations), which tags the donation with it.
// extraFields lets callers attach extra IsraelGives params (e.g. routing).
export function buildIgPayment({
  profile, amount, frequency = '1', currency = DEFAULT_CURRENCY,
  projectId, extraFields = {}, token, returnPath,
}) {
  const cur = currencyMeta(currency);
  const foreign = cur.code !== DEFAULT_CURRENCY;
  // Trimmed before the emptiness check: "   " is as blank as "" to their
  // address validation, and would otherwise be sent as a filled-in value.
  const zipcode = String(profile.zipCode || '').trim().slice(0, 7);
  const action =
    `${IG_IFRAME_URL}?MakeDonation=1&AmutaGovId=${IG_AMUTA_ID}&fb=1` +
    // We render our own "cover the transaction fee" checkbox (default
    // checked) instead of IsraelGives' — it can't be defaulted to checked
    // via URL params, so theirs is hidden and `amount` already includes the
    // fee when the donor has opted in.
    `&hidecovermyfee=1` +
    // A QUERY param, not a POST field: their page ignores `Projectid` in the
    // body (verified against the live page). And only ever one of OUR ids —
    // a project id belonging to another amuta silently swaps the page to that
    // amuta, so the charge would go to them.
    (projectId ? `&Projectid=${encodeURIComponent(projectId)}` : '') +
    `&successurl=${encodeURIComponent(buildSuccessUrl(token, returnPath))}`;
  const fields = {
    post: '1', // required by the iframe API
    fname: profile.firstName || '',
    lname: profile.lastName || '',
    email: profile.email || '',
    // Decided by the number, not by `foreign`: an overseas donor giving in
    // shekels still has an overseas phone, and the Israeli normalizer would
    // truncate it to a stranger's number.
    phone: isIsraeliPhone(profile.phone)
      ? normalizeIlPhone(profile.phone)
      : normalizeForeignPhone(profile.phone),
    address: profile.address || '',
    city: profile.city || '',
    // Their `AdressIsMandatory()` makes street + postcode compulsory for
    // CAD/GBP/EUR — and a POSTed flow hides every donor field they'd be typed
    // into (the inputs carry class `postHide`), so an address we don't send is
    // an address that stays empty and silently blocks the charge. Hence the
    // street/postcode inputs on the foreign donor form. `zipcode` is their
    // param name for it (verified against the live page; `zip`/`postalcode`
    // are ignored), and their input is maxlength=7.
    //
    // OMITTED ENTIRELY when we have no postcode, rather than sent empty. A
    // shekel donation never collects one, and posting `zipcode=` is not the
    // same to their page as not posting it: an empty value is a filled-in
    // blank, which is what their address validation rejects. Sending it
    // unconditionally failed every shekel charge. Keep this conditional —
    // with it, a shekel donation's payload is byte-identical to the one that
    // was charging successfully before foreign currencies were added.
    ...(zipcode ? { zipcode } : {}),
    // OMITTED ENTIRELY for shekel donations, rather than hardcoded to Israel
    // (104) as it used to be. Two problems with the old hardcode, both
    // confirmed against IsraelGives' own live page source:
    //
    // 1. It mis-recorded every shekel donor as Israeli, including the
    //    overseas donors who give an Israeli NGO shekels.
    // 2. Their page only enforces its Israeli-phone-format check
    //    (`!phone.startsWith("0") || phone.length > 10`) when a country is
    //    selected: `if ($("#ddlcountry").val() && $('#tbcontacttelephone').val())`.
    //    A hardcoded 104 made that true for every shekel donation, blocking
    //    any donor whose real phone wasn't Israeli-shaped.
    //
    // Posting no `countryid` at all leaves their `#ddlcountry` unselected
    // (`value=""`) — verified by POSTing this exact payload to their live
    // endpoint — which makes `$("#ddlcountry").val()` falsy and skips that
    // whole check, for every phone shape, without their `skipphonevalidation`
    // escape hatch. It doesn't newly require an address either: their
    // `AdressIsMandatory()` is keyed on currency (CAD/GBP/EUR only), not on
    // country having a value.
    //
    // Foreign currencies are untouched: the donor's own selection, falling
    // back to the country IsraelGives pairs with the currency.
    ...(foreign ? { countryid: profile.countryId || cur.countryId } : {}),
    sum: String(amount || ''),
    currency: cur.id,
    frequency,
    freq: frequency, // the newer /pay/ template's docs name this param "freq", not "frequency"
    ...extraFields,
  };
  return { action, fields };
}
